
import { memo, useEffect, useState } from "react";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept } from "./CodeableConceptEntry";
import PeriodEntry, { displayStringFromFHIRPeriod, getDateTimeDisplay } from "./PeriodEntry";
import QuantityEntry, { displayStringFromFHIRQuantity } from "./QuantityEntry";
import RangeEntry, { displayStringFromFHIRRange } from "./RangeEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import ArrayEntry from "./ArrayEntry";
import DatatypeSelector from "./DatatypeSelector";
import PositiveIntEntry from "./PositiveIntEntry";
import DecimalEntry from "./DecimalEntry";
import CodeEntry from "./CodeEntry";
import CodeArrayEntry from "./CodeArrayEntry";
import UnsignedIntEntry from "./UnsignedIntEntry";

//expected input of Timing datatype (object with {event:[dateTime], 
//       repeat: {bounds[x]:Duration/Range/Period, count:positiveInt, countMax:positiveInt, duration:decimal, durationMax:decimal,
//                durationUnit:code, frequency:positiveInt, frequencyMax:positiveInt, period:decimal, periodMax:decimal,
//                periodUnit:code, dayOfWeek:[code], timeOfDay:[time], when:[code], offset:unsignedInt}, 
//       code:CodeableConcept})
//expected output if matching data structure with matching datatypes is noted in the code script
//expected output if string datatype is "{timing} [ERROR: Timing data not in expected Timing datatype.]"
//expected output if missing (undefined/null/'') is ''
//expected output if NaN, empty {}, empty [], or [a,b] is "[ERROR: Timing data not interpretable.]"
//expected output if not otherwise specified is "[ERROR: Timing data not interpretable.]"
const displayStringFromFHIRTiming = (timing) => {
    if (timing === undefined || timing === null || timing === '') {
        return ""
    }
    if (typeof timing === "object") {
        let timingDisplay = "";
        if (timing.event?.length > 0) {
            timingDisplay = "At " + getDateTimeDisplay(timing.event[0]) + ", ";
        }
        if (timing.event?.length > 1) {
            let i = 1;
            while (i < timing.event.length - 1) {
                timingDisplay += getDateTimeDisplay(timing.event[i]) + ", ";
                i += 1;
            }
            timingDisplay += "and " + getDateTimeDisplay(timing.event[i]) + "; ";
        }
        if (timing.code) {
            timingDisplay += "[[[ " + displayStringFromFHIRCodeableConcept(timing.code) + " ]]]";
        }
        if (timingDisplay && timing.repeat) {
            timingDisplay += "; ";
        }
        if (timing.repeat) {
            timingDisplay += "repeat ";
            if (timing.repeat.count) {
                timingDisplay += timing.repeat.count + " times ";
            }
            if (timing.repeat.countMax) {
                timingDisplay += "up to " + timing.repeat.countMax + " times ";
            }
            if (timing.repeat.duration) {
                timingDisplay += "for " + timing.repeat.duration + " ";
            }
            if (timing.repeat.durationMax) {
                timingDisplay += "for up to " + timing.repeat.durationMax + " ";
            }
            if (timing.repeat.durationUnit) {
                timingDisplay += timing.repeat.durationUnit + " ";
            }
            if (timing.repeat.frequency) {
                timingDisplay += timing.repeat.frequency + " times per ";
            }
            if (timing.repeat.frequencyMax) {
                timingDisplay += "up to " + timing.repeat.frequencyMax + " times per ";
            }
            if (timing.repeat.period) {
                timingDisplay += timing.repeat.period + " ";
            }
            if (timing.repeat.periodMax) {
                timingDisplay += "up to " + timing.repeat.periodMax + " ";
            }
            if (timing.repeat.periodUnit) {
                timingDisplay += timing.repeat.periodUnit + " ";
            }
            if (timing.repeat.dayOfWeek) {
                timingDisplay += "on days of week (" + timing.repeat.dayOfWeek.toString() + ") ";
            }
            if (timing.repeat.timeOfDay) {
                timingDisplay += "at times of day (" + timing.repeat.timeOfDay.toString() + ") ";
            }
            if (timing.repeat.offset) {
                timingDisplay += timing.repeat.offset + " minutes after "
            }
            if (timing.repeat.when) {
                timingDisplay += "when (" + timing.repeat.when.toString() + ") ";
            }
            if (timing.repeat.boundsDuration) {
                timingDisplay += "within " + displayStringFromFHIRQuantity(timing.repeat.boundsDuration);
            }
            if (timing.repeat.boundsRange) {
                timingDisplay += "within " + displayStringFromFHIRRange(timing.repeat.boundsRange);
            }
            if (timing.repeat.boundsPeriod) {
                timingDisplay += "within " + displayStringFromFHIRPeriod(timing.repeat.boundsPeriod);
            }
        }
        let timingDisplayLength = timingDisplay.length;
        if (timingDisplay.substring(timingDisplayLength - 2) === ", ") {
            timingDisplay = timingDisplay.substring(0, timingDisplayLength - 2);
        } else if (timingDisplay.substring(timingDisplayLength - 2) === "; ") {
            timingDisplay = timingDisplay.substring(0, timingDisplayLength - 2);
        } else if (timingDisplay.substring(timingDisplayLength - 1) === " ") {
            timingDisplay = timingDisplay.substring(0, timingDisplayLength - 1);
        }
        if (timingDisplay && typeof timingDisplay === 'string') {
            return timingDisplay;
        }
    }
    if (typeof timing === "string") {
        return `${timing} [ERROR: Timing data not in expected Timing datatype.]`
    }
    return "[ERROR: Timing data not interpretable.]";
}

const displayStringFromFHIRTimingRepeat = (timingRepeat) => {
    
    if (timingRepeat === undefined || timingRepeat === null || timingRepeat === '') {
        return ""
    }
    if (typeof timingRepeat === "object") {
        let timingRepeatDisplay = "repeat ";

            if (timingRepeat.count) {
                timingRepeatDisplay += timingRepeat.count + " times ";
            }
            if (timingRepeat.countMax) {
                timingRepeatDisplay += "up to " + timingRepeat.countMax + " times ";
            }
            if (timingRepeat.duration) {
                timingRepeatDisplay += "for " + timingRepeat.duration + " ";
            }
            if (timingRepeat.durationMax) {
                timingRepeatDisplay += "for up to " + timingRepeat.durationMax + " ";
            }
            if (timingRepeat.durationUnit) {
                timingRepeatDisplay += timingRepeat.durationUnit + " ";
            }
            if (timingRepeat.frequency) {
                timingRepeatDisplay += timingRepeat.frequency + " times per ";
            }
            if (timingRepeat.frequencyMax) {
                timingRepeatDisplay += "up to " + timingRepeat.frequencyMax + " times per ";
            }
            if (timingRepeat.period) {
                timingRepeatDisplay += timingRepeat.period + " ";
            }
            if (timingRepeat.periodMax) {
                timingRepeatDisplay += "up to " + timingRepeat.periodMax + " ";
            }
            if (timingRepeat.periodUnit) {
                timingRepeatDisplay += timingRepeat.periodUnit + " ";
            }
            if (timingRepeat.dayOfWeek) {
                timingRepeatDisplay += "on days of week (" + timingRepeat.dayOfWeek.toString() + ") ";
            }
            if (timingRepeat.timeOfDay) {
                timingRepeatDisplay += "at times of day (" + timingRepeat.timeOfDay.toString() + ") ";
            }
            if (timingRepeat.offset) {
                timingRepeatDisplay += timingRepeat.offset + " minutes after "
            }
            if (timingRepeat.when) {
                timingRepeatDisplay += "when (" + timingRepeat.when.toString() + ") ";
            }
            if (timingRepeat.boundsDuration) {
                timingRepeatDisplay += "within " + displayStringFromFHIRQuantity(timingRepeat.boundsDuration);
            }
            if (timingRepeat.boundsRange) {
                timingRepeatDisplay += "within " + displayStringFromFHIRRange(timingRepeat.boundsRange);
            }
            if (timingRepeat.boundsPeriod) {
                timingRepeatDisplay += "within " + displayStringFromFHIRPeriod(timingRepeat.boundsPeriod);
            }
        
        let timingDisplayLength = timingRepeatDisplay.length;
        if (timingRepeatDisplay.substring(timingDisplayLength - 2) === ", ") {
            timingRepeatDisplay = timingRepeatDisplay.substring(0, timingDisplayLength - 2);
        } else if (timingRepeatDisplay.substring(timingDisplayLength - 2) === "; ") {
            timingRepeatDisplay = timingRepeatDisplay.substring(0, timingDisplayLength - 2);
        } else if (timingRepeatDisplay.substring(timingDisplayLength - 1) === " ") {
            timingRepeatDisplay = timingRepeatDisplay.substring(0, timingDisplayLength - 1);
        }
        if (timingRepeatDisplay && typeof timingRepeatDisplay === 'string') {
            return timingRepeatDisplay;
        }
    }
    if (typeof timingRepeat === "string") {
        return `${timingRepeat} [ERROR: TimingRepeat data not in expected Timing datatype.]`
    }
    return "[ERROR: Timing data not interpretable.]";
}

const TimingEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {
  let startingTiming = { 'event': [], 'repeat': "", 'code': "" };
  if (!startingValue) {
    startingValue = "";
  } else {
    if (startingValue.extension) { startingTiming.extension = startingValue.extension; }
    if (startingValue.event) { startingTiming.event = startingValue.event; }
    if (startingValue.repeat) { startingTiming.repeat = startingValue.repeat; }
    if (startingValue.code) { startingTiming.code = startingValue.code; }
  }

  const [timingState, setTimingState] = useState(JSON.parse(JSON.stringify(startingTiming || {})));
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);
  const timingDotCodeValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/timing-abbreviation", code: "C", display: "Continuous (frequency)" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "BID", display: "BID" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "TID", display: "TID" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "QID", display: "QID" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "AM", display: "AM" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "PM", display: "PM" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "QD", display: "QD" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "QOD", display: "QOD" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "Q1H", display: "every hour" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "Q2H", display: "every 2 hours" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "Q3H", display: "every 3 hours" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "Q4H", display: "Q4H" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "Q6H", display: "Q6H" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "Q8H", display: "every 8 hours" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "BED", display: "at bedtime" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "WK", display: "weekly" },
    { system: "https://terminology.hl7.org/4.0.0/CodeSystem-v3-GTSAbbreviation.html", code: "MO", display: "monthly" }
  ]

  useEffect((() => {
    if (Object.keys(timingState).length) {
      let newTiming = {};
      if (timingState.extension) { newTiming.extension = timingState.extension; }
      if (Array.isArray(timingState.event) && timingState.event.length) {
        newTiming.event = timingState.event;
      }
      if (timingState.repeat && Object.keys(timingState.repeat).length) {
        newTiming.repeat = timingState.repeat;
      }
      if (timingState.code && Object.keys(timingState.code).length) {
        newTiming.code = timingState.code;
      }
      if (Object.keys(newTiming).length === 0) {
        newTiming = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newTiming }; });
    }
  }), [timingState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRTiming(startingTiming)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        {(timingState.event === undefined || Array.isArray(timingState.event) || timingState.event === null) &&
          <ArrayEntry datatype='dateTime' elementName='event' fieldLabel='Date/Time'
            startingValue={timingState.event} setResourceState={setTimingState} />}
        <TimingRepeatEntry elementName='repeat' fieldLabel='Repeat'
          startingValue={timingState.repeat} setResourceState={setTimingState} />
        <CodeableConceptEntry elementName='code' fieldLabel='Timing Abbreviation'
          valueSet={timingDotCodeValueSet}
          startingValue={timingState.code} setResourceState={setTimingState} />
      </div>
    </>
  }
})

const timingRepeatDotBoundsAllowedDatatypes = ['Duration', 'Range', 'Period'];
const timingRepeatPeriodUnitAllowedValues = ['s', 'min', 'h', 'd', 'wk', 'mo', 'a'];
const timingRepeatDayOfWeekAllowedValues = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const timingRepeatWhenAllowedValues = ['HS', 'WAKE', 'C', 'CM', 'CD', 'CV', 'AC', 'ACM', 'ACD', 'ACV', 'PC', 'PCM', 'PCD', 'PCV'];

const TimingRepeatEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, inTableCell, debug, startCollapsed }) => {
  let startingTimingRepeat = {
    'boundsDuration': "", 'boundsRange': "", 'boundsPeriod': "", 'count': "", 'countMax': "",
    'duration': "", 'durationMax': "", 'durationUnit': "", 'frequency': "", 'frequencyMax': "",
    'period': "", 'periodMax': "", 'periodUnit': "", 'dayOfWeek': [], 'timeOfDay': [],
    'when': [], 'offset': ""
  };
  let startingBoundsDatatype = 'none';
  if (!startingValue) {
    startingValue = "";
  } else {
    if (startingValue.extension) { startingTimingRepeat.extension = startingValue.extension; }
    if (startingValue.boundsDuration) {
      startingTimingRepeat.boundsDuration = startingValue.boundsDuration;
      startingBoundsDatatype = 'Duration';
    }
    if (startingValue.boundsRange) {
      startingTimingRepeat.boundsRange = startingValue.boundsRange;
      startingBoundsDatatype = 'Range';
    }
    if (startingValue.boundsPeriod) {
      startingTimingRepeat.boundsPeriod = startingValue.boundsPeriod;
      startingBoundsDatatype = 'Period';
    }
    if (startingValue.count) { startingTimingRepeat.count = startingValue.count; }
    if (startingValue.countMax) { startingTimingRepeat.countMax = startingValue.countMax; }
    if (startingValue.duration) { startingTimingRepeat.duration = startingValue.duration; }
    if (startingValue.durationMax) { startingTimingRepeat.durationMax = startingValue.durationMax; }
    if (startingValue.durationUnit) { startingTimingRepeat.durationUnit = startingValue.durationUnit; }
    if (startingValue.frequency) { startingTimingRepeat.frequency = startingValue.frequency; }
    if (startingValue.frequencyMax) { startingTimingRepeat.frequencyMax = startingValue.frequencyMax; }
    if (startingValue.period) { startingTimingRepeat.period = startingValue.period; }
    if (startingValue.periodMax) { startingTimingRepeat.periodMax = startingValue.periodMax; }
    if (startingValue.periodUnit) { startingTimingRepeat.periodUnit = startingValue.periodUnit; }
    if (startingValue.dayOfWeek) { startingTimingRepeat.dayOfWeek = startingValue.dayOfWeek; }
    if (startingValue.timeOfDay) { startingTimingRepeat.timeOfDay = startingValue.timeOfDay; }
    if (startingValue.when) { startingTimingRepeat.when = startingValue.when; }
    if (startingValue.offset) { startingTimingRepeat.offset = startingValue.offset; }
  }

  const [timingRepeatState, setTimingRepeatState] = useState(JSON.parse(JSON.stringify(startingTimingRepeat || {})));

  const [boundsDatatypeState, setBoundsDatatypeState] = useState(startingBoundsDatatype);
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect((() => {
    if (Object.keys(timingRepeatState).length) {
      let newTimingRepeat = {};
      if (timingRepeatState.extension) { newTimingRepeat.extension = timingRepeatState.extension; }
      if (boundsDatatypeState === 'Duration' && timingRepeatState.boundsDuration && Object.keys(timingRepeatState.boundsDuration).length) {
        newTimingRepeat.boundsDuration = timingRepeatState.boundsDuration;
      }
      if (boundsDatatypeState === 'Range' && timingRepeatState.boundsRange && Object.keys(timingRepeatState.boundsRange).length) {
        newTimingRepeat.boundsRange = timingRepeatState.boundsRange;
      }
      if (boundsDatatypeState === 'Period' && timingRepeatState.boundsPeriod && Object.keys(timingRepeatState.boundsPeriod).length) {
        newTimingRepeat.boundsPeriod = timingRepeatState.boundsPeriod;
      }
      if (timingRepeatState.count) { newTimingRepeat.count = timingRepeatState.count; }
      if (timingRepeatState.countMax) { newTimingRepeat.countMax = timingRepeatState.countMax; }
      if (timingRepeatState.duration) { newTimingRepeat.duration = timingRepeatState.duration; }
      if (timingRepeatState.durationMax) { newTimingRepeat.durationMax = timingRepeatState.durationMax; }
      if (timingRepeatState.durationUnit) { newTimingRepeat.durationUnit = timingRepeatState.durationUnit; }
      if (timingRepeatState.frequency) { newTimingRepeat.frequency = timingRepeatState.frequency; }
      if (timingRepeatState.frequencyMax) { newTimingRepeat.frequencyMax = timingRepeatState.frequencyMax; }
      if (timingRepeatState.period) { newTimingRepeat.period = timingRepeatState.period; }
      if (timingRepeatState.periodMax) { newTimingRepeat.periodMax = timingRepeatState.periodMax; }
      if (timingRepeatState.periodUnit) { newTimingRepeat.periodUnit = timingRepeatState.periodUnit; }
      if (Array.isArray(timingRepeatState.dayOfWeek) && timingRepeatState.dayOfWeek.length) {
        newTimingRepeat.dayOfWeek = timingRepeatState.dayOfWeek;
      }
      if (Array.isArray(timingRepeatState.timeOfDay) && timingRepeatState.timeOfDay.length) {
        newTimingRepeat.timeOfDay = timingRepeatState.timeOfDay;
      }
      if (Array.isArray(timingRepeatState.when) && timingRepeatState.when.length) {
        newTimingRepeat.when = timingRepeatState.when;
      }
      if (timingRepeatState.offset) { newTimingRepeat.offset = timingRepeatState.offset; }
      if (Object.keys(newTimingRepeat).length === 0) {
        newTimingRepeat = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newTimingRepeat }; });
    }
  }), [timingRepeatState, boundsDatatypeState]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRTimingRepeat(startingTimingRepeat)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <p style={{ marginBottom: "0px" }}><b>Bounds for Timing Schedule:</b></p>
        <div style={{ marginLeft: "24px" }}>
          <DatatypeSelector elementXName='bounds[x]' allowedDatatypes={timingRepeatDotBoundsAllowedDatatypes}
            datatypeState={boundsDatatypeState} setDatatypeState={setBoundsDatatypeState} />
          {boundsDatatypeState === 'Duration' &&
            <QuantityEntry elementName='boundsDuration' fieldLabel={'Duration for Timing Schedule'}
              dataEntryStyle={"Duration"} inTableCell={inTableCell}
              startingValue={timingRepeatState.boundsDuration} setResourceState={setTimingRepeatState} />}
          {boundsDatatypeState === 'Range' &&
            <RangeEntry elementName='boundsRange' fieldLabel={'Range for Timing Schedule'}
              dataEntryStyle={dataEntryStyle}
              startingValue={timingRepeatState.boundsRange} setResourceState={setTimingRepeatState} />}
          {boundsDatatypeState === 'Period' &&
            <PeriodEntry elementName='boundsPeriod' fieldLabel={'Period for Timing Schedule'}
              inTableCell={inTableCell}
              startingValue={timingRepeatState.boundsPeriod} setResourceState={setTimingRepeatState} />}
        </div>
        <PositiveIntEntry elementName='count' fieldLabel='Number of times to repeat'
          startingValue={timingRepeatState.count} setResourceState={setTimingRepeatState} />
        <PositiveIntEntry elementName='countMax' fieldLabel='Maximum number of times to repeat'
          startingValue={timingRepeatState.countMax} setResourceState={setTimingRepeatState} />
        <DecimalEntry elementName='duration' fieldLabel='Duration'
          startingValue={timingRepeatState.duration} setResourceState={setTimingRepeatState} />
        <DecimalEntry elementName='durationMax' fieldLabel='Duration Maximum'
          startingValue={timingRepeatState.durationMax} setResourceState={setTimingRepeatState} />
        <CodeEntry elementName='durationUnit' fieldLabel='Duration unit'
          allowedValues={timingRepeatPeriodUnitAllowedValues} dataEntryStyle='dropdown'
          startingValue={timingRepeatState.durationUnit} setResourceState={setTimingRepeatState} />
        <PositiveIntEntry elementName='frequency' fieldLabel='Frequency (number of repetitions per period)'
          startingValue={timingRepeatState.frequency} setResourceState={setTimingRepeatState} />
        <PositiveIntEntry elementName='frequencyMax' fieldLabel='Frequency Maximum'
          startingValue={timingRepeatState.frequencyMax} setResourceState={setTimingRepeatState} />
        <DecimalEntry elementName='period' fieldLabel='Period (duration for frequency per period)'
          startingValue={timingRepeatState.period} setResourceState={setTimingRepeatState} />
        <DecimalEntry elementName='periodMax' fieldLabel='Period Maximum'
          startingValue={timingRepeatState.periodMax} setResourceState={setTimingRepeatState} />
        <CodeEntry elementName='periodUnit' fieldLabel='Period unit'
          allowedValues={timingRepeatPeriodUnitAllowedValues} dataEntryStyle='dropdown'
          startingValue={timingRepeatState.periodUnit} setResourceState={setTimingRepeatState} />
        {(timingRepeatState.dayOfWeek === undefined || Array.isArray(timingRepeatState.dayOfWeek) || timingRepeatState.dayOfWeek === null) &&
          <CodeArrayEntry datatype='code' elementName='dayOfWeek' fieldLabel='Day of Week'
            allowedValues={timingRepeatDayOfWeekAllowedValues} dataEntryStyle='dropdown'
            startingValue={timingRepeatState.dayOfWeek} setResourceState={setTimingRepeatState} />}
        {(timingRepeatState.timeOfDay === undefined || Array.isArray(timingRepeatState.timeOfDay) || timingRepeatState.timeOfDay === null) &&
          <ArrayEntry datatype='time' elementName='timeOfDay' fieldLabel='Time of Day'
            startingValue={timingRepeatState.timeOfDay} setResourceState={setTimingRepeatState} />}
        {(timingRepeatState.when === undefined || Array.isArray(timingRepeatState.when) || timingRepeatState.when === null) &&
          <CodeArrayEntry datatype='code' elementName='when' fieldLabel='When'
            allowedValues={timingRepeatWhenAllowedValues} dataEntryStyle='dropdown'
            startingValue={timingRepeatState.when} setResourceState={setTimingRepeatState} />}
        <UnsignedIntEntry elementName='offset' fieldLabel='Offset (minutes before or after)'
          startingValue={timingRepeatState.offset} setResourceState={setTimingRepeatState} />
      </div>
    </>
  }
})

export default TimingEntry;
export { displayStringFromFHIRTiming };